import { useEffect, useRef, useState } from "react";
import { env } from "../lib/env";
import CopyButton from "../components/CopyButton";
import ChatIcon from "../icons/ChatIcon";
import CheckIcon from "../icons/CheckIcon";
import LinkIcon from "../icons/LinkIcon";
import { routeToHash, type HelpSectionId } from "../lib/routing";
import styles from "./HelpPage.module.css";

interface Props {
  onBack: () => void;
  backLabel?: string;
  onContribute: () => void;
  // Opens only this section, overriding the default-open one.
  initialSection?: HelpSectionId;
  // User clicks only; not fired for sections open on mount.
  onSectionOpen?: (id: HelpSectionId) => void;
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      className={[styles.chevron, expanded && styles.chevronOpen].filter(Boolean).join(" ")}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Shorter than the titles so the link button and toggle don't share an accessible name.
const SECTION_LINK_LABELS: Record<HelpSectionId, string> = {
  "chat-cowork": "Claude (Chat & Cowork)",
  "claude-code": "Claude Code",
  "usage-figures": "Usage figures",
  "contributing": "Contributing",
};

function SectionLinkButton({ id }: { id: HelpSectionId }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const label = SECTION_LINK_LABELS[id];

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    const url = `${window.location.origin}${window.location.pathname}${routeToHash({ page: "help", section: id })}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? `Copied link to ${label} section` : `Copy link to ${label} section`}
      className={[styles.sectionLinkButton, copied && styles.sectionLinkButtonCopied].filter(Boolean).join(" ")}
    >
      {copied ? <CheckIcon width="14" height="14" /> : <LinkIcon width="14" height="14" />}
    </button>
  );
}

function Section({
  id, title, defaultOpen, onOpen, children,
}: {
  id: HelpSectionId; title: string; defaultOpen?: boolean; onOpen?: (id: HelpSectionId) => void; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) onOpen?.(id);
  };
  return (
    <div id={id} className={styles.section}>
      <div className={[styles.sectionHeader, open && styles.sectionHeaderOpen].filter(Boolean).join(" ")}>
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={open}
          className={styles.sectionToggle}
        >
          <span className={styles.sectionTitle}>{title}</span>
          <ChevronIcon expanded={open} />
        </button>
        <SectionLinkButton id={id} />
      </div>
      {open && (
        <div className={styles.sectionContent}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function HelpPage({ onBack, backLabel, onContribute, initialSection, onSectionOpen }: Props) {
  // rAF: AppContent's route-change focus() on <main> runs after this (parent
  // effect) and would scroll back to the top.
  useEffect(() => {
    if (!initialSection) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(initialSection)?.scrollIntoView?.({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
    // Mount-only; AppContent remounts this page on each help navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const marketplaceName = env.REACT_APP_MARKETPLACE_NAME;
  // CLI commands (Claude Code) still register the marketplace by URL.
  // The Chat & Cowork desktop client gets it pre-installed via enterprise admin
  // tooling, so there's no GUI "add marketplace" URL to display.
  const catalogUrl = env.REACT_APP_CATALOG_GIT_URL || "";

  return (
    <div>
      <button type="button" onClick={onBack} className={styles.backLink}>
        &larr; {backLabel ?? "Back to Marketplace"}
      </button>

      <h1 className={styles.title}>Help &amp; Setup</h1>
      <p className={styles.intro}>
        The CAI marketplace is already installed in Claude (Chat &amp; Cowork) — find plug-ins in
        Organization Plugins. Claude Code registers it via CLI.
        The same marketplace serves every surface.
      </p>

      <p className={styles.supportBanner}>
        <ChatIcon className={styles.supportBannerIcon} />
        <span>
          Questions or need a hand? Join our{" "}
          <a
            href="https://coxauto.slack.com/archives/C0B4Y1HJXS8"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            support Slack channel
          </a>
          .
        </span>
      </p>

      <div className={styles.sections}>

        <Section id="chat-cowork" title="Claude (Chat &amp; Cowork) — start here" defaultOpen={initialSection ? initialSection === "chat-cowork" : true} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            The CAI marketplace is pre-installed for everyone via Claude&rsquo;s enterprise admin tooling —
            there&rsquo;s no marketplace to add. Plug-ins appear directly in Organization Plugins.
          </p>

          <h3 className={styles.subhead}>Install a plug-in</h3>
          <ol className={styles.list}>
            <li>In Claude, go to <strong>Customize → Organization Plugins → + Icon</strong></li>
            <li>Search for the plug-in and click <strong>+</strong> to install it — it&rsquo;s available in your next conversation</li>
          </ol>

          <h3 className={styles.subhead}>Install an individual skill</h3>
          <p className={styles.pFlush}>
            Individual skills unpacked from a plug-in aren&rsquo;t listed in Organization Plugins — the only
            way to use one in Chat or Cowork is to download it and add it manually. (You can also download
            any plug-in this way as a fallback.)
          </p>
          <ol className={styles.list}>
            <li>On a detail page, click <strong>Download</strong> to save the file</li>
            <li><strong>Plug-in:</strong> in Claude, go to <strong>Customize → Upload Plugin</strong> and select the .plugin file</li>
            <li><strong>Skill:</strong> go to <strong>Customize → Skills → Create skill → Upload a skill</strong> and select the skill.md file</li>
          </ol>

          <h3 className={styles.subhead}>Good to know</h3>
          <p className={styles.pFlush}>
            Claude runs both skills and plug-ins. A plug-in&rsquo;s skills, slash commands, and MCP
            connectors all work in Chat — only hooks and sub-agents are Cowork-only, so a plug-in that
            ships those runs fully in Cowork.
          </p>
          <p className={styles.calloutWarning}>
            <strong>Installing is separate from runtime.</strong> A plug-in&rsquo;s bundled MCP connector routes
            through Anthropic&rsquo;s cloud in Chat and Cowork, so a connector that targets an internal-only
            server won&rsquo;t reach it there — even though the plug-in installs fine. Such connectors work in
            Claude Code, which runs locally.
          </p>

          <h3 className={styles.subhead}>Links</h3>
          <ul className={styles.list}>
            <li><a href="https://coxautoinc.sharepoint.com/sites/service-engineering-enablement/delivery-platform/SitePages/Installing-Plugins-and-Skills.aspx" target="_blank" rel="noopener noreferrer" className={styles.link} aria-label="Video: Installing plugins and skills in Claude Desktop (opens in new window)">Video: Installing plugins and skills in Claude Desktop</a></li>
            <li><a href="https://support.claude.com/en/articles/13837440-use-plugins-in-claude" target="_blank" rel="noopener noreferrer" className={styles.link}>Use plugins in Claude</a></li>
            <li><a href="https://claude.com/plugins" target="_blank" rel="noopener noreferrer" className={styles.link}>Browse plugins on claude.com</a></li>
          </ul>
        </Section>

        <Section id="claude-code" title="Claude Code (CLI)" defaultOpen={initialSection === "claude-code"} onOpen={onSectionOpen}>
          <h3 className={styles.subheadFirst}>One-time setup: add the marketplace</h3>
          <p>Register this marketplace so Claude Code knows where to find plug-ins:</p>
          <CopyButton text={`claude plugin marketplace add ${catalogUrl}`} />

          <h3 className={styles.subhead}>Installing a plug-in</h3>
          <p>Copy the install command from any plug-in's detail page, or run:</p>
          <p><strong>From any terminal</strong> (outside a Claude Code session):</p>
          <CopyButton text={`claude plugin install <name>@${marketplaceName}`} />
          <p>The plug-in will be available next time you start Claude Code.</p>

          <p className={styles.pSpaced}><strong>Inside a Claude Code session:</strong></p>
          <CopyButton text={`/plugin install <name>@${marketplaceName}`} />
          <p>
            Then run <code className={styles.inlineCode}>/reload-plugins</code> to
            activate immediately without restarting.
          </p>

          <h3 className={styles.subhead}>Links</h3>
          <ul className={styles.list}>
            <li><a href="https://code.claude.com/docs/en/discover-plugins" target="_blank" rel="noopener noreferrer" className={styles.link}>Discover and install plugins</a></li>
            <li><a href="https://code.claude.com/docs/en/plugins" target="_blank" rel="noopener noreferrer" className={styles.link}>Create plugins</a></li>
            <li><a href="https://code.claude.com/docs/en/cli-reference" target="_blank" rel="noopener noreferrer" className={styles.link}>CLI reference</a></li>
          </ul>
        </Section>

        {/* Usage figures. Closes #264's help-content criterion ("the limitation that
            skill-less plugins are unmeasurable is documented in help content"), which its
            own delivery left undone. Every reason listed below is a MEASURED failure mode
            from #264's first production pull, not a hypothetical: coverage is 42.4%, so
            "no usage yet" is the majority state and the first thing a plug-in author asks
            about. Keep in sync with contracts/contract-7-adoption.ts. */}
        <Section id="usage-figures" title="Usage figures (active users)" defaultOpen={initialSection === "usage-figures"} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            Cards, list rows, and detail pages show <strong>active users</strong> &mdash; how many
            distinct people used a plug-in or skill in the last 30 days. It counts people, not
            installs and not invocations, so one person using something every day counts once.
          </p>
          <p className={styles.pFlush}>
            The figures come from Claude&rsquo;s enterprise usage data, which lags by about three
            days. The <strong>as of</strong> date shown with the figures is the last day included &mdash;
            always trust that date over &ldquo;today&rdquo;.
          </p>

          <h3 className={styles.subhead}>Why something shows &ldquo;no usage yet&rdquo;</h3>
          <p className={styles.pFlush}>
            Most catalog entries show this, and it is usually not a sign that nobody wants your
            plug-in. It means we have no figure to show, for one of these reasons:
          </p>
          <ul className={styles.list} aria-label="Reasons usage is unavailable">
            <li><strong>It was read, not run.</strong> Claude counts a skill only when it is explicitly activated. A reference skill that Claude reads as background context &mdash; documentation, examples, standards &mdash; is genuinely useful and never registers as usage.</li>
            <li><strong>There is no skill to measure.</strong> Usage is currently reported per skill, so a plug-in that ships only MCP connectors, slash commands, hooks, or sub-agents has nothing to count yet.</li>
            <li><strong>Nobody used it in the last 30 days.</strong> The plain explanation, and only one of several &mdash; which is why a missing figure is never shown as a zero.</li>
          </ul>
          <p className={styles.pFlush}>
            Usage data identifies a skill by name only, with no repo attached, so when two
            different plug-ins ship a skill with the same name, we can&rsquo;t tell their usage
            apart. Rather than guess or drop it, we combine it: the figure shown is the total
            across every skill with that name, marked with an asterisk (<strong>*</strong>) and
            a note explaining it&rsquo;s a combined count.
          </p>
          <p className={styles.calloutWarning}>
            <strong>&ldquo;No usage yet&rdquo; never means zero.</strong> The underlying data only records
            things that were used, so a genuine zero never reaches us. A missing figure means
            unknown, and there is one state for it rather than implying a measurement we do not have.
          </p>

          <h3 className={styles.subhead}>Sorting by Most Used</h3>
          <p className={styles.pFlush}>
            <strong>Most Used</strong> orders the catalog by active users, highest first. Entries with
            no figure sort to the bottom &mdash; they are never hidden, so a plug-in published today
            still appears while it waits for its first usage data. While searching, results stay in{" "}
            <strong>Best Match</strong> (relevance) order unless you pick a different sort.
          </p>
        </Section>

        <Section id="contributing" title="Contributing Plug-Ins and Skills" defaultOpen={initialSection === "contributing"} onOpen={onSectionOpen}>
          <p className={styles.pLead}>
            Publishing is automatic. Any push to the default branch of a <strong>public or internal</strong> (non-private)
            org-owned GitHub repo that touches the <code className={styles.inlineCode}>.claude-plugin/plugin.json</code> manifest
            is ingested by the marketplace via webhook — no extra step. Every later push to the default
            branch that touches the manifest keeps the catalog entry up to date, and removing the manifest deactivates it.
          </p>

          <h3 className={styles.subhead}>Two ways to publish</h3>
          <ol className={styles.list}>
            <li><strong>From a repo:</strong> Add a <code className={styles.inlineCode}>.claude-plugin/plugin.json</code> to any public or internal org repo and push it to the <strong>default branch</strong> — the marketplace catalogs it automatically.</li>
            <li>
              <strong>Web form — no repo needed:</strong> Publishing a single standalone skill?{" "}
              <button
                type="button"
                onClick={onContribute}
                className={styles.linkInline}
              >
                Contribute a skill
              </button>{" "}
              to upload its files directly — no repository required.
            </li>
          </ol>

          <h3 className={styles.subhead}>Repo eligibility rules</h3>
          <p className={styles.pFlush}>Not every repo or every push gets cataloged:</p>
          <ul className={styles.list} aria-label="Repo eligibility rules">
            <li><strong>GitHub App installed in the org.</strong> The marketplace only receives webhooks from orgs where our GitHub App is installed — without it, no pushes are ever seen. Post in our{" "}
              <a href="https://coxauto.slack.com/archives/C0B4Y1HJXS8" target="_blank" rel="noopener noreferrer" className={styles.link}>support Slack channel</a>
              {" "}to request installation for your org.
            </li>
            <li><strong>Must be org-owned.</strong> Repos under personal GitHub accounts are excluded regardless of visibility.</li>
            <li><strong>Must be public or internal.</strong> Private repos are never scanned.</li>
            <li><strong>No forks.</strong> Forked repos are excluded — only the canonical source repo is cataloged.</li>
            <li><strong>Not archived.</strong> Archived repos are excluded from push processing. Archiving also triggers an automatic catalog deactivation via the GitHub App webhook.</li>
            <li><strong>Default branch push required.</strong> Both initial cataloging and every later update require a push to the default branch that includes the <code className={styles.inlineCode}>.claude-plugin/plugin.json</code> file — pushes to feature branches are never cataloged, and changes that don&rsquo;t touch the manifest won&rsquo;t trigger ingestion.</li>
          </ul>

          {/* Limits mirror processor-lambda field-mapper (MAX_NAME_LENGTH etc.); keep in sync (#565). */}
          <h3 className={styles.subhead}>Manifest requirements</h3>
          <p className={styles.pFlush}>
            The marketplace requires a few fields in <code className={styles.inlineCode}>.claude-plugin/plugin.json</code>{" "}
            beyond what Claude Code itself needs. A manifest that misses any of these isn&rsquo;t cataloged. For most
            problems (a missing or malformed field, or an invalid structure) the specific reason shows on your My Skills
            page. The one exception is a file that isn&rsquo;t valid JSON at all: it can&rsquo;t be tied back to your
            account, so if your plug-in isn&rsquo;t showing up, first confirm the manifest parses:
          </p>
          <ul className={styles.list} aria-label="Manifest requirements">
            <li><strong><code className={styles.inlineCode}>name</code></strong> — required. 1&ndash;100 characters, made up of letters, digits, and <code className={styles.inlineCode}>-</code> <code className={styles.inlineCode}>.</code> <code className={styles.inlineCode}>_</code>, and must start with a letter or digit. Internal spaces and other punctuation are rejected. This is published verbatim as the install name.</li>
            <li><strong><code className={styles.inlineCode}>description</code></strong> — required. 1&ndash;2000 characters, non-empty.</li>
            <li><strong><code className={styles.inlineCode}>version</code></strong> — required. Must be valid <a href="https://semver.org/" target="_blank" rel="noopener noreferrer" className={styles.link}>semver</a> (for example <code className={styles.inlineCode}>1.0.0</code>).</li>
            <li><strong><code className={styles.inlineCode}>schemaVersion</code></strong> — optional. If set, its major version must be <code className={styles.inlineCode}>1</code>.</li>
            <li><strong>Valid JSON, valid structure.</strong> The file must parse as JSON (no trailing commas or comments) and every declared component must satisfy Claude Code&rsquo;s own plugin schema — for example <code className={styles.inlineCode}>skills</code> paths must be strings starting with <code className={styles.inlineCode}>./</code>. Run <code className={styles.inlineCode}>claude plugin validate</code> locally to check.</li>
          </ul>
          <p className={styles.pFlush}>
            Every other field (keywords, categories, author, license, homepage, and so on) is optional and is
            sanitized or dropped if invalid rather than causing a skip.
          </p>

          <h3 className={styles.subhead}>Names must be unique across the whole marketplace</h3>
          <p className={styles.pFlush}>
            <code className={styles.inlineCode}>name</code> is published as the install name and must be unique
            catalog-wide, not just within your repo &mdash; two different plug-ins can&rsquo;t share one. Before you
            push, search the catalog for the name you intend to use. If it&rsquo;s already taken by another plug-in,
            the push is refused (your plug-in isn&rsquo;t cataloged) and the reason shows on your My Skills page.
            Pick a different name and push again.
          </p>

          <h3 className={styles.subhead}>Opt an eligible repo out</h3>
          <p className={styles.pFlush}>
            Want a repo that would otherwise be cataloged to stay out of the marketplace? Add the
            GitHub topic <code className={styles.inlineCode}>plugin-marketplace-exclude</code> to the
            repository — edit the <strong>About</strong> section on the repo&rsquo;s main page and add it
            under <strong>Topics</strong>. The plug-in is delisted everywhere the marketplace publishes —
            this site and the install catalogs for Claude Code and Claude (Chat &amp; Cowork) — usually
            within a minute. Remove the topic to list it again.
          </p>
          <p className={styles.pFlush}>
            The topic (case-insensitive) is the whole signal. Editing a repository&rsquo;s topics requires
            the Maintain or Admin role, so only a repo maintainer or admin can opt it in or out. The
            catalog entry is hidden, not deleted, and the repo&rsquo;s own visibility is unchanged.
          </p>

          <h3 className={styles.subhead}>Links</h3>
          <ul className={styles.list}>
            <li><a href="https://code.claude.com/docs/en/plugins" target="_blank" rel="noopener noreferrer" className={styles.link}>Plugin authoring guide</a></li>
          </ul>
        </Section>

      </div>
    </div>
  );
}
